/* eslint-disable react/destructuring-assignment */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Button } from 'antd';
import { parse, stringify } from 'qs';
import axios from 'axios';

import InboxTable from '../components/inbox';
import Header from '../components/header';
import { chromeReceivedColumns, chromeUploadedColumns } from './records/shared/constants';
import { rowSelected } from '../actions/inboxTableActions';
import { getLoginInfo } from '../actions/auth';
import { changeInboxPage } from '../actions/inbox';
import { getReceivedRecordsByPage, getUploadedRecordsByPage } from '../actions/records';
import { loadChromeViewerSelection } from '../actions/chromeViewerRecords';
import { isUsableChromeViewerSignedUrl } from '../utils/chromeViewerRequestLifecycle';
import SingleFlightRequest from '../utils/singleFlightRequest';

const selectionButtonsContainerStyle = {
  display: 'block',
  marginTop: '20px',
  position: 'fixed',
  bottom: '0px',
  right: '0px',
};

const btnStyle = {
  marginRight: '10px',
  marginLeft: '10px',
  marginBottom: '10px',
};

const archiveBtnStyle = {
  marginRight: '10px',
  marginBottom: '10px',
};

const stringifyOptions = {
  format: 'RFC1738',
  addQueryPrefix: true,
  sort: (a, b) => (a.localeCompare(b)),
};

class ChromeRecordSelector extends Component {
  static ASYNC_COUNT_RETRY_DELAY_MS = 2000;

  constructor(props) {
    super(props);
    this.state = {
      searchQuery: '',
      asyncCountMetadata: null,
      asyncCountMetadataKey: null,
    };
    this.asyncCountAbortController = null;
    this.asyncCountResolvedKey = null;
    this.asyncCountPendingKey = null;
    this.asyncCountRetryTimeout = null;
    this.selectionRequest = new SingleFlightRequest();
  }

  componentDidMount() {
    this.selectorRoute()?.fetchData(1, 5);
    this.focusHeadingAfterViewerReturn();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.location.pathname !== this.props.location.pathname) {
      this.abortSelectionRequest();
      this.resetAsyncCount();
      if (this.state.searchQuery) this.setState({ searchQuery: '' });
      this.selectorRoute()?.fetchData(1, 5);
      return;
    }

    this.syncAsyncCount(prevProps, prevState);
  }

  componentWillUnmount() {
    this.abortAsyncCountRequest();
    this.abortSelectionRequest();
  }

  isReceivedSelector = (props = this.props) => props.location.pathname === '/app/chrome/received_selector';

  selectorRoute = (props = this.props) => {
    const routes = {
      '/app/chrome/received_selector': {
        mode: 'Received',
        tableColumns: chromeReceivedColumns,
        fetchData: this.fetchReceivedRecordsByPage,
        data: props.receivedData || [],
      },
      '/app/chrome/uploaded_selector': {
        mode: 'Uploaded',
        tableColumns: chromeUploadedColumns,
        fetchData: props.getUploadedRecordsByPage,
        data: props.uploadedData || [],
      },
    };

    return routes[props.location.pathname] || null;
  };

  focusHeadingAfterViewerReturn = () => {
    const { history, location } = this.props;
    if (!location.state?.focusChromeSelectorHeading) return;

    const heading = document.querySelector('.main-header-text');
    if (!heading) return;

    heading.setAttribute('tabindex', '-1');
    heading.focus();
    const { focusChromeSelectorHeading, ...state } = location.state;
    history.replace({ ...location, state });
  };

  getPage = () => {
    const { headers } = this.props;
    return headers ? parseInt(headers.page, 10) || 1 : 1;
  };

  getPerPage = () => {
    const { headers } = this.props;
    return headers ? parseInt(headers['per-page'], 10) || 5 : 5;
  };

  hasMore = () => {
    const { headers } = this.props;
    return headers ? headers['has-more'] === 'true' : false;
  };

  countState = () => {
    const { headers } = this.props;
    return headers ? headers['count-state'] || 'exact' : 'exact';
  };

  displayCountState = () => {
    if (this.countState() === 'async' && this.currentAsyncCountMetadata()) {
      return 'exact';
    }

    return this.countState();
  };

  getTotalPages = () => {
    const asyncCountMetadata = this.currentAsyncCountMetadata();
    if (asyncCountMetadata) {
      return asyncCountMetadata.total_pages;
    }

    if (this.countState() === 'async') {
      return this.getPage() + (this.hasMore() ? 1 : 0);
    }

    const { headers } = this.props;
    return headers ? parseInt(headers['total-pages'], 10) || 1 : 1;
  };

  getTotalResults = (rowsLength = this.selectorRoute()?.data.length || 0) => {
    const asyncCountMetadata = this.currentAsyncCountMetadata();
    if (asyncCountMetadata) {
      return asyncCountMetadata.total_count;
    }

    if (this.countState() === 'async') {
      return ((this.getPage() - 1) * this.getPerPage()) + rowsLength + (this.hasMore() ? 1 : 0);
    }

    const { headers } = this.props;
    return headers ? parseInt(headers.total, 10) || 0 : 0;
  };

  selectorQueryParams = (searchQuery = this.state.searchQuery) => {
    const normalizedQuery = (searchQuery || '').replace(/^&/, '?');
    const params = parse(normalizedQuery, { ignoreQueryPrefix: true });
    params.include_signed_replies = 'true';
    params.page_size = '5';
    params.record_selector = 'true';
    delete params.page;
    delete params.sort;
    delete params.sort_dir;
    return params;
  };

  asyncCountQueryKeyForSearch = (searchQuery = this.state.searchQuery) => stringify(
    this.selectorQueryParams(searchQuery),
    stringifyOptions,
  );

  asyncCountQueryKey = (props = this.props, searchQuery = this.state.searchQuery) => {
    const headers = props.headers || {};
    if (!this.isReceivedSelector(props) || (headers['count-state'] || 'exact') !== 'async') return null;

    return this.asyncCountQueryKeyForSearch(searchQuery);
  };

  activeAsyncCountQueryKey = () => {
    if (!this.isReceivedSelector()) return null;

    const responseKey = this.asyncCountQueryKey();
    if (responseKey !== null) return responseKey;

    return this.asyncCountSupported() ? this.asyncCountQueryKeyForSearch() : null;
  };

  currentAsyncCountMetadata = () => {
    if (this.countState() !== 'async') return null;

    const currentKey = this.activeAsyncCountQueryKey();
    if (currentKey === null || this.state.asyncCountMetadataKey !== currentKey) return null;

    return this.state.asyncCountMetadata;
  };

  asyncCountSupported = () => this.isReceivedSelector();

  primeAsyncCount = (searchQuery = this.state.searchQuery) => {
    if (!this.isReceivedSelector()) return;

    const currentKey = this.asyncCountQueryKeyForSearch(searchQuery);
    if (this.asyncCountResolvedKey === currentKey || this.asyncCountPendingKey === currentKey) return;

    this.resetAsyncCount();
    if (!this.asyncCountSupported()) return;

    this.fetchAsyncCount(currentKey);
  };

  abortAsyncCountRequest = () => {
    if (this.asyncCountRetryTimeout) {
      clearTimeout(this.asyncCountRetryTimeout);
      this.asyncCountRetryTimeout = null;
    }
    if (this.asyncCountAbortController) {
      this.asyncCountAbortController.abort();
      this.asyncCountAbortController = null;
    }
    this.asyncCountPendingKey = null;
  };

  resetAsyncCount = () => {
    this.abortAsyncCountRequest();
    this.asyncCountResolvedKey = null;
    if (this.state.asyncCountMetadata) {
      this.setState({ asyncCountMetadata: null, asyncCountMetadataKey: null });
    }
  };

  syncAsyncCount = (prevProps, prevState) => {
    if (!this.isReceivedSelector()) return;

    const currentKey = this.asyncCountQueryKey(this.props, this.state.searchQuery);
    const previousKey = this.asyncCountQueryKey(prevProps, prevState.searchQuery);

    if (currentKey !== previousKey) {
      if (!(currentKey !== null && (
        this.asyncCountResolvedKey === currentKey ||
        this.asyncCountPendingKey === currentKey
      ))) {
        this.resetAsyncCount();
      }
    }

    if (currentKey === null) return;
    if (this.asyncCountResolvedKey === currentKey || this.asyncCountPendingKey === currentKey) return;

    this.fetchAsyncCount(currentKey);
  };

  fetchAsyncCount = async (queryKey, attempt = 0) => {
    if (attempt === 0) {
      this.abortAsyncCountRequest();
    }

    const abortController = new AbortController();
    this.asyncCountAbortController = abortController;
    this.asyncCountPendingKey = queryKey;

    try {
      const response = await axios.get(`/api/v1/receivers/count${queryKey}`, {
        signal: abortController.signal,
      });

      if (this.asyncCountPendingKey !== queryKey) return;
      if (this.activeAsyncCountQueryKey() !== queryKey) {
        this.asyncCountPendingKey = null;
        this.asyncCountAbortController = null;
        return;
      }

      this.asyncCountResolvedKey = queryKey;
      this.asyncCountPendingKey = null;
      this.asyncCountAbortController = null;
      this.setState({ asyncCountMetadata: response.data, asyncCountMetadataKey: queryKey });
    } catch (error) {
      if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || axios.isCancel?.(error)) {
        return;
      }

      if (this.asyncCountPendingKey !== queryKey) {
        return;
      }

      this.asyncCountAbortController = null;

      if (attempt < 1) {
        this.asyncCountRetryTimeout = setTimeout(() => {
          this.asyncCountRetryTimeout = null;
          this.fetchAsyncCount(queryKey, attempt + 1);
        }, this.asyncCountRetryDelay());
        return;
      }

      this.asyncCountPendingKey = null;
    }
  };

  asyncCountRetryDelay = () => {
    const baseDelay = ChromeRecordSelector.ASYNC_COUNT_RETRY_DELAY_MS;
    return baseDelay + Math.floor(Math.random() * baseDelay);
  };

  handleSelection = async (archive) => {
    const { loadChromeViewerSelection: fetchRecordSelection, selectedData } = this.props;
    if (!selectedData || selectedData.length === 0) return;
    const {
      file_token: fileToken,
      patient_name: patientName,
      patient_dob: patientDOB,
      document_title: documentTitle,
    } = selectedData[0];
    const selectionRequest = this.selectionRequest.start();
    if (!selectionRequest) return;

    try {
      const response = await fetchRecordSelection(fileToken, {
        archive,
        signal: selectionRequest.signal,
      });
      if (selectionRequest.signal.aborted
        || !response?.success
        || !isUsableChromeViewerSignedUrl(response.data?.signed_url)) return;

      // On the Chrome Extension, the medsender-content.js script listens for
      // this event and passes the validated response URL to the background script.
      const signedUrl = response.data.signed_url;
      const name = patientName || '';
      const dob = patientDOB || '';

      // Pass in fileName so we can have patient name and DOB show up in the EMR as the filename so
      // they can import the record easily without having to go back and forth between the EMR and
      // chrome extension.
      let fileName = '';
      if (name) {
        fileName = name.replace(/[^a-zA-Z0-9]+/g, '_');
      } else {
        fileName = 'record';
      }

      if (dob) {
        fileName += `_${dob.replace(/[^a-zA-Z0-9]+/g, '_')}.pdf`;
      } else {
        fileName += '.pdf';
      }

      const triggerChange = new CustomEvent('chromeExtFileSelected', {
        detail: { url: signedUrl, fileName, documentTitle },
      });
      document.dispatchEvent(triggerChange);
    } finally {
      this.selectionRequest.finish(selectionRequest.key);
    }
  }

  abortSelectionRequest = () => {
    this.selectionRequest.abort();
  };

  handleClose = () => {
    const triggerChange = new CustomEvent('chromeExtFileSelected', {
      detail: { url: 'WINDOW_CLOSED' },
    });
    document.dispatchEvent(triggerChange);
    window.close();
  }

  handlePageChange = (pageParams) => {
    const params = parse(pageParams, { ignoreQueryPrefix: true });
    const selectedPage = parseInt(params.page, 10);
    const { searchQuery } = this.state;
    this.selectorRoute()?.fetchData(selectedPage, 5, searchQuery);
    // If anything was previously selected and the page is changed, then it will continue
    // to be selected and the "Choose" button will remain enabled.
    //
    // Clear so that on a new page change, Choose button becomes disabled again
    this.props.rowSelected([]);
  }

  reloadPage = async () => {
    const {
      getLoginInfo: handleGetLoginInfo,
    } = this.props;
    await handleGetLoginInfo();
    window.location.reload();
  }

  fetchReceivedRecordsByPage = (page, pageSize, searchQuery = '') => {
    const { getReceivedRecordsByPage } = this.props;
    const queryParams = `&include_signed_replies=true${searchQuery}&record_selector=true`;
    const response = getReceivedRecordsByPage(page, pageSize, queryParams);
    this.primeAsyncCount(searchQuery);
    return response;
  }

  fetchRecordsWithQuery = (selectedData, dataOnPage, searchQuery = '') => {
    this.setState({ searchQuery });
    this.selectorRoute()?.fetchData(selectedData, dataOnPage, searchQuery);
  }

  render() {
    const selectorRoute = this.selectorRoute();
    if (!selectorRoute) return null;

    const { data, tableColumns, mode } = selectorRoute;
    const {
      history,
      location,
    } = this.props;

    const currentPage = this.getPage();
    const totalPages = this.getTotalPages();
    const totalResults = this.getTotalResults(data.length);
    const countState = this.displayCountState();
    let params = {};
    if (location.search) {
      params = parse(location.search, { ignoreQueryPrefix: true });
    }
    const paramTools = {
      params,
      stringify,
      stringifyOptions,
      push: history.push,
    };

    const isOnReceivedSelector = this.isReceivedSelector();
    const chromeSelectorLink = isOnReceivedSelector ? '/app/chrome/uploaded_selector' : '/app/chrome/received_selector';
    const labelName = isOnReceivedSelector ? 'Uploaded' : 'Received';

    if (!data.length > 0) {
      return (
        <>
          <Header
            main={`${mode} Selector`}
            subtext="Choose a record"
            locationPath={[]}
            chromeSelectorPage={mode}
            fetchData={this.fetchRecordsWithQuery}
            selectedPage={currentPage}
            rightButtons={[{
              text: `Switch to ${labelName} Selector`,
              disabled: false,
              func: () => window.location.assign(chromeSelectorLink),
              style: {
                backgroundColor: '#662C88',
                color: '#fff',
                height: '40px',
            },
            }]}
          />
        </>
      );
    }

    return (
      <>
        <Header
          main={`${mode} Selector`}
          subtext="Choose a record"
          locationPath={[]}
          chromeSelectorPage={mode}
          fetchData={this.fetchRecordsWithQuery}
          selectedPage={currentPage}
          rightButtons={[{
            text: `Switch to ${labelName} Selector`,
            disabled: false,
            func: () => window.location.assign(chromeSelectorLink),
            style: {
              backgroundColor: '#662C88',
              color: '#fff',
              height: '40px',
            },
          }]}
        />
        <InboxTable
          rows={data.filter(row => row.file_token)}
          page={currentPage}
          totalPages={totalPages}
          actions={{
            changeInboxPage: this.props.changeInboxPage,
            getRecords: this.handlePageChange,
          }}
          push={history.push}
          mode={mode}
          userIsOnChromeExtensionViewer={true}
          countState={countState}
          totalResults={totalResults}
          selectedData={this.props.selectedData}
          paramTools={paramTools}
          rowSelected={this.props.rowSelected}
          columnTitles={tableColumns}
          enableSelectAll={false}
          getReplyData={() => {
            // TODO
            // Add support for displaying replies here and being able to select them
            // To view replies, remove the .filter array call in the rows prop
          }}
          getSmsData={() => {}}
          getReferralReplyData={() => {}}
          setNewRecordViewerOpen={() => {
            // No-op: Chrome extension selector uses row selection + "Choose" button
            // instead of opening the NewRecordViewer on row click
          }}
        />
        <div style={selectionButtonsContainerStyle}>
          <Button size="large" onClick={this.handleClose}>Cancel</Button>
          <Button
            size="large"
            type="primary"
            style={btnStyle}
            onClick={() => this.handleSelection(false)}
            disabled={this.props.selectedData.length === 0}
          >
            Choose
          </Button>
          <Button
            size="large"
            type="primary"
            style={archiveBtnStyle}
            onClick={() => this.handleSelection(true)}
            disabled={this.props.selectedData.length === 0}
          >
            Choose & Archive
          </Button>
        </div>
      </>
    );
  }
}

ChromeRecordSelector.defaultProps = {
  headers: null,
};

ChromeRecordSelector.propTypes = {
  getReceivedRecordsByPage: PropTypes.func.isRequired,
  getUploadedRecordsByPage: PropTypes.func.isRequired,
  rowSelected: PropTypes.func.isRequired,
  changeInboxPage: PropTypes.func.isRequired,
  selectedData: PropTypes.array.isRequired,
  loadChromeViewerSelection: PropTypes.func.isRequired,
  uploadedData: PropTypes.array.isRequired,
  receivedData: PropTypes.array.isRequired,
  headers: PropTypes.object,
  getLoginInfo: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    replace: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string,
    state: PropTypes.shape({
      focusChromeSelectorHeading: PropTypes.bool,
    }),
  }).isRequired,
};

export default connect(state => ({
  receivedData: state.records.received_data,
  uploadedData: state.records.uploaded_data,
  selectedData: state.inboxTable.recordData,
  headers: state.records.headers,
}), {
  getReceivedRecordsByPage,
  getUploadedRecordsByPage,
  loadChromeViewerSelection,
  rowSelected,
  changeInboxPage,
  getLoginInfo,
})(ChromeRecordSelector);

export { ChromeRecordSelector };
